import CopyLinkButton from '@/components/Discord/CopyLinkButton'
import { useState } from 'react'
import GuidelineTooltip from '../../../components/Discord/GuidelineTooltip'
import styles from '@/components/Discord/DiscordDocument.module.css'
import DiscordDocument from '@/components/Discord/DiscordDocument'

function DiscordDeceptivePracticesPolicyPage() {
  const [language, setLanguage] = useState<'en' | 'cs'>('cs')
  const [maliciousConductExamplesOpen, setMaliciousConductExamplesOpen] = useState(false)
  const [fraudServicesExamplesOpen, setFraudServicesExamplesOpen] = useState(false)

  return (
    <DiscordDocument
      language={language}
      onLanguageChange={setLanguage}
      heading={
        <div className={styles.titleSection}>
          <h1 className={styles.title}>
            {language === 'cs' ? 'Politika klamavých praktik' : 'Deceptive Practices Policy'}
          </h1>
        </div>
      }
      notice={
        <>
          <div className={styles.disclaimer}>
            <div className={styles.disclaimerCard} lang="cs">
              <h3>Důležité upozornění</h3>
              <p>
                Nejsme přidruženi ani spojeni s Discord Inc. Toto je pouze komunitní překlad
                oficiální stránky o politice klamavých praktik Discord dostupné na{' '}
                <a
                  href="https://discord.com/safety/deceptive-practices-policy-explainer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  discord.com/safety/deceptive-practices-policy-explainer
                </a>
                .
              </p>
              <p>
                Tento překlad nemusí být aktuální s oficiální politikou. Pokud si všimnete rozdílu
                mezi oficiální stránkou a touto stránkou, prosím, dejte nám vědět na našem Discord
                serveru.
              </p>
            </div>
            <div className={styles.disclaimerCard} lang="en">
              <h3>Important Notice</h3>
              <p>
                We are not affiliated with or associated with Discord Inc. This is only a community
                translation of the official Discord deceptive practices policy available at{' '}
                <a
                  href="https://discord.com/safety/deceptive-practices-policy-explainer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  discord.com/safety/deceptive-practices-policy-explainer
                </a>
                .
              </p>
              <p>
                This translation may not be up to date with the official policy. If you notice a
                difference between the official page and this page, please let us know on our
                Discord server.
              </p>
            </div>
          </div>
        </>
      }
    >
      {/* Disclaimer section - always show both languages */}

      {/* Quick Summary - Czech only */}
      {language === 'cs' && (
        <details className={styles.quickSummary} id="quick-summary">
          <summary>
            <h2>Rychlé shrnutí politiky</h2>
          </summary>
          <CopyLinkButton
            sectionId={'quick-summary'}
            language={language}
            className={styles.copyBtn}
          />

          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <h3>Finanční podvody</h3>
              <ul>
                <li>Žádné Ponziho schéma nebo pyramidové hry</li>
                <li>Zákaz podvodů s investicemi</li>
                <li>Žádné romance scams nebo phishing</li>
                <li>Zákaz manipulace s trhem</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Škodlivé chování</h3>
              <ul>
                <li>Žádný phishing nebo malware</li>
                <li>Zákaz DoS útoků</li>
                <li>Nepoužívejte Discord CDN pro škodlivé soubory</li>
                <li>Žádné krádeže účtů nebo informací</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Podvodné služby</h3>
              <ul>
                <li>Neprodávejte kradené účty nebo údaje</li>
                <li>Žádné padělání nebo praní peněz</li>
                <li>Zákaz koordinovaných podvodů</li>
                <li>Žádné návody na páchání podvodů</li>
              </ul>
            </div>
          </div>
          <p className={styles.summaryNote}>
            Toto je pouze stručné shrnutí. Pro úplné znění čtěte celý dokument níže.
          </p>
        </details>
      )}

      {/* Policy Body */}
      <div className={styles.policyBody}>
        {language === 'cs' ? (
          <>
            <section id="financial-scams" className={styles.policySection}>
              <div className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'financial-scams'}
                  language={language}
                  className={styles.copyBtn}
                />
                <h2>Finanční podvody</h2>
                <p>
                  Online podvody mohou způsobit finanční škodu lidem v reálném světě. Usilovně
                  pracujeme na tom, aby Discord zůstal bezpečným místem pro uživatele k propojení s
                  jejich komunitami — tak se nebudou muset obávat lidí, kteří se je snaží oklamat z
                  nelegálních nebo nečestných důvodů. Jednotlivci nesmí používat naši službu k
                  propagaci, koordinaci nebo provádění jakéhokoli typu finančního podvodu.
                </p>
                <p>
                  Finanční podvody se obecně pokoušejí využít jednotlivce vytvářením falešných slibů
                  zboží, služeb nebo jiných finančních výhod. Tyto schémata se snaží vydělat
                  podvodem obětí nebo jejich přiměním k odhalení citlivých informací. Neúplný seznam
                  finančních podvodů zahrnuje: Ponziho schémata, pyramidové hry, podvody s
                  předplacenými poplatky, manipulaci s trhem (včetně schémat "pump-and-dump"),
                  romance scams, pracovní podvody a phishingové podvody.
                </p>
              </div>
            </section>

            <section id="malicious-conduct" className={styles.policySection}>
              <div className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'malicious-conduct'}
                  language={language}
                  className={styles.copyBtn}
                />
                <h2>Škodlivé chování</h2>
                <p>
                  Chceme, aby si uživatelé užívali bezpečnou a zabezpečenou zkušenost s naší
                  službou. V souladu s tím nepovolujeme činnosti, které by mohly poškodit nebo
                  ohrozit bezpečnost účtu, počítačové sítě nebo systému. Termín "škodlivé chování"
                  používáme k označení phishingu, používání malwaru a útoků typu denial-of-service
                  (DoS), mimo jiné chování.
                </p>
                <p>
                  <strong>Phishing</strong> je klamavá technika používaná k oklamání ostatních k
                  odhalení citlivých informací (jako jsou přihlašovací údaje nebo platební údaje).
                  Jednotlivci mohou phishingem získávat informace pomocí manipulovaných odkazů nebo
                  falešných webových stránek, které vypadají skutečně, aby přesvědčili cíle, že jsou
                  důvěryhodné. <strong>Malware</strong> je jakýkoli typ softwaru používaný ke
                  škodlivým účelům — včetně krádeže přihlašovacích údajů k účtu a dalších citlivých
                  informací.
                  <strong>Útoky typu denial-of-service (DoS)</strong> zaplavují cíl provozem, aby
                  byl zdroj dočasně nebo trvale nedostupný.
                </p>
              </div>
            </section>

            <section className={styles.examplesSection}>
              <button
                className={styles.dropdownToggle}
                onClick={() => setMaliciousConductExamplesOpen(!maliciousConductExamplesOpen)}
                aria-expanded={maliciousConductExamplesOpen}
                type="button"
              >
                <span>Například nesmíte zveřejňovat, sdílet nebo se zapojovat do:</span>
                <span className={styles.dropdownIcon}>
                  {maliciousConductExamplesOpen ? '−' : '+'}
                </span>
              </button>

              {maliciousConductExamplesOpen && (
                <div className={styles.dropdownContent}>
                  <ul>
                    <li>
                      Používání phishingových technik, jako jsou falešné webové stránky,
                      manipulované odkazy nebo přesměrování URL ke shromažďování citlivých informací
                    </li>
                    <li>Odesílání škodlivého kódu ostatním jako součást injekčního útoku</li>
                    <li>
                      Vytváření nebo distribuce malwaru ke krádeži informací; udělení
                      neautorizovaného přístupu k účtu, síti nebo systému; nebo blokování přístupu k
                      počítači
                    </li>
                    <li>
                      Používání sítě pro doručování obsahu (CDN) Discordu k hostování škodlivých
                      souborů, malwaru nebo phishingových zdrojů pro pozdější distribuci
                    </li>
                    <li>
                      Vlastnictví nebo provozování infrastruktury na platformě na podporu
                      malwarových operací
                    </li>
                    <li>
                      Účast na činnosti, která přímo pomáhá nebo usnadňuje jakékoli z výše uvedených
                      chování
                    </li>
                  </ul>
                </div>
              )}
            </section>

            <section id="fraud-services" className={styles.policySection}>
              <div className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'fraud-services'}
                  language={language}
                  className={styles.copyBtn}
                />
                <h2>Podvodné služby</h2>
                <p>
                  Trvale odstraníme podvodné služby a veškeré snahy o usnadnění nelegálního chování,
                  jakmile se o nich dozvíme. Termín <strong>podvodné služby</strong> používáme k
                  označení koordinované činnosti, která se přímo pokouší nebo je součástí většího
                  pokusu o podvodné generování zisku na úkor ostatních. Zisk může znamenat peněžní
                  nebo majetkový zisk zajištěný od třetí strany.
                </p>
                <p>
                  Jednou z činností zakázaných podle této politiky je nabízení přístupu k nelegálně
                  získanému zboží — včetně, ale nejen: přihlašovacích údajů k účtu, informací o
                  kreditních kartách, osobních identifikačních údajů a cracknutého komoditního
                  zboží. Koordinované snahy o podvádění podniků, předražování, padělání a praní
                  peněz také nejsou povoleny.
                </p>
              </div>
            </section>

            <section className={styles.examplesSection}>
              <button
                className={styles.dropdownToggle}
                onClick={() => setFraudServicesExamplesOpen(!fraudServicesExamplesOpen)}
                aria-expanded={fraudServicesExamplesOpen}
                type="button"
              >
                <span>Například nesmíte zveřejňovat, sdílet nebo se zapojovat do:</span>
                <span className={styles.dropdownIcon}>{fraudServicesExamplesOpen ? '−' : '+'}</span>
              </button>

              {fraudServicesExamplesOpen && (
                <div className={styles.dropdownContent}>
                  <ul>
                    <li>
                      Obchodování, prodej nebo pokus o nákup nelegálně získaných:
                      <ul>
                        <li>Přihlašovacích údajů k účtu, jako jsou uživatelská jména a hesla</li>
                        <li>
                          Finančních informací, jako jsou informace o kreditních kartách a
                          bankovních účtech
                        </li>
                        <li>
                          Osobních údajů, jako jsou e-mailové adresy, telefonní čísla a čísla
                          sociálního pojištění
                        </li>
                      </ul>
                    </li>
                    <li>
                      Poskytování přístupu k nástrojům, které usnadňují prolomení účtů nebo platební
                      podvody
                    </li>
                    <li>
                      Účast na koordinovaných činnostech k podvádění jednotlivce, podniku nebo
                      organizace
                      <ul>
                        <li>
                          To zahrnuje provádění nákupů s ukradenými platebními údaji, zneužívání
                          dodací logistiky, sociální inženýrství zaměstnanců podpory společnosti a
                          zapojení do podvodů s chargebacky
                        </li>
                      </ul>
                    </li>
                    <li>Poskytování pokynů nebo seznamů technik, jak páchat podvody</li>
                    <li>
                      Účast v organizovaném schématu přeprodeje zboží s velkým ziskem, jako je
                      předražování
                    </li>
                    <li>
                      Prodej, obchodování nebo nákup padělků nebo účast na padělátelských operacích
                    </li>
                    <li>Účast ve schématech praní peněz</li>
                    <li>Prodej, směna nebo distribuce padělaného zboží</li>
                  </ul>
                </div>
              )}
            </section>

            <p>
              <a href="https://discord.com/safety/understanding-and-avoiding-common-scams">
                Jak rozpoznat běžné podvody (anglicky)
              </a>
            </p>
            <section id="how-to-avoid" className={styles.policySection}>
              <div className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'how-to-avoid'}
                  language={language}
                  className={styles.copyBtn}
                />
                <h2>Jak se vyhnout porušení těchto politik</h2>
                <p>
                  Porušení těchto politik bereme vážně a prohlášení o touze nebo úmyslu zapojit se
                  do nelegálních činností vezmeme za bernou minci.
                </p>
                <ul>
                  <li>
                    Neposílejte škodlivé odkazy. To zahrnuje "IP grabbery" a další nástroje, které
                    se snaží shromažďovat citlivé informace jiných lidí
                  </li>
                  <li>
                    Nezapojujte se do podvodných činností. To znamená nějakým způsobem se snažit
                    generovat zisk na úkor ostatních
                  </li>
                  <li>
                    Neúčastněte se komunit prodávajících komoditní zboží, které mohlo být ukradeno
                    nebo nelegálně získáno
                  </li>
                </ul>
                <p>
                  Nahlaste a opusťte servery věnované jakémukoli z výše popsaného porušujícího
                  obsahu. Discord může také podniknout kroky proti účtům, které jsou přítomny na
                  porušujících serverech.
                </p>
              </div>
            </section>

            <section id="how-to-report" className={styles.policySection}>
              <div className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'how-to-report'}
                  language={language}
                  className={styles.copyBtn}
                />
                <h2>Jak nahlásit porušení těchto politik</h2>
                <p>
                  Nahlašování porušení bezpečnosti je kriticky důležité pro udržení bezpečnosti vás
                  a širší komunity Discord. Všichni uživatelé Discordu mohou nahlásit porušení zásad
                  v aplikaci podle pokynů{' '}
                  <a href="/discord/safety/reporting-abusive-behavior-to-discord">zde</a>. Pro více
                  informací o těchto politikách se prosím podívejte na naše{' '}
                  <a href="/discord/guidelines">Pokyny komunity</a>{' '}
                  <GuidelineTooltip guidelineNumber="20" language={language}>
                    <a href="/discord/guidelines#guideline-20" className={styles.guidelineLink}>
                      #20
                    </a>
                  </GuidelineTooltip>
                  ,{' '}
                  <GuidelineTooltip guidelineNumber="21" language={language}>
                    <a href="/discord/guidelines#guideline-21" className={styles.guidelineLink}>
                      #21
                    </a>
                  </GuidelineTooltip>
                  {' a '}
                  <GuidelineTooltip guidelineNumber="22" language={language}>
                    <a href="/discord/guidelines#guideline-22" className={styles.guidelineLink}>
                      #22
                    </a>
                  </GuidelineTooltip>
                  .
                </p>
              </div>
            </section>
          </>
        ) : (
          <>
            <section id="financial-scams" className={styles.policySection}>
              <div className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'financial-scams'}
                  language={language}
                  className={styles.copyBtn}
                />
                <h2>Financial Scams</h2>
                <p>
                  Online scams can cause financial harm to people in the real world. We work hard to
                  keep Discord a safe place for users to connect with their communities — that way,
                  they won't have to worry about people trying to trick them for illegal or
                  dishonest reasons. Individuals may not use our service to promote, coordinate, or
                  perform any type of financial scam.
                </p>
                <p>
                  Financial scams generally attempt to take advantage of individuals by making false
                  promises of goods, services, or other financial benefits. These schemes try to
                  turn a profit by defrauding victims or tricking them into revealing sensitive
                  information. A non-exhaustive list of financial scams includes: Ponzi schemes,
                  pyramid schemes, advance-fee fraud, market manipulation (including "pump-and-dump"
                  schemes), romance scams, employment scams, and phishing scams.
                </p>
              </div>
            </section>

            <section id="malicious-conduct" className={styles.policySection}>
              <div className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'malicious-conduct'}
                  language={language}
                  className={styles.copyBtn}
                />
                <h2>Malicious Conduct</h2>
                <p>
                  We want users to enjoy a safe, secure experience on our service. Accordingly, we
                  do not allow activity that could damage or compromise the security of an account,
                  computer network, or system. We use the term "malicious conduct" to refer to
                  phishing, the use of malware, and denial-of-service (DoS) attacks, among other
                  conduct.
                </p>
                <p>
                  <strong>Phishing</strong> is a deceptive technique used to trick others into
                  revealing sensitive information (such as logins or payment details). Individuals
                  may phish for information with manipulated links or fake websites that appear
                  genuine to convince targets they are trustworthy. <strong>Malware</strong>
                  is any type of software used for malicious purposes — including theft of account
                  credentials and other sensitive information.{' '}
                  <strong>Denial-of-service (DoS)</strong> attacks flood a target with traffic to
                  make the resource temporarily or permanently unavailable.
                </p>
              </div>
            </section>

            <section className={styles.examplesSection}>
              <button
                className={styles.dropdownToggle}
                onClick={() => setMaliciousConductExamplesOpen(!maliciousConductExamplesOpen)}
                aria-expanded={maliciousConductExamplesOpen}
                type="button"
              >
                <span>For example, you may not post, share, or engage in:</span>
                <span className={styles.dropdownIcon}>
                  {maliciousConductExamplesOpen ? '−' : '+'}
                </span>
              </button>

              {maliciousConductExamplesOpen && (
                <div className={styles.dropdownContent}>
                  <ul>
                    <li>
                      Using phishing techniques, such as using fake websites, manipulated links, or
                      URL redirection to collect sensitive information
                    </li>
                    <li>Sending malicious code to others as part of an injection attack</li>
                    <li>
                      Creating or distributing malware to steal information; grant unauthorized
                      access to an account, network, or system; or block access to a computer
                    </li>
                    <li>
                      Using Discord's content-delivery network (CDN) to host malicious files,
                      malware, or phishing resources for later distribution
                    </li>
                    <li>
                      Owning or operating on-platform infrastructure to aid malware operations
                    </li>
                    <li>
                      Participating in activity that directly aids or helps facilitate any of the
                      behaviors listed above
                    </li>
                  </ul>
                </div>
              )}
            </section>

            <section id="fraud-services" className={styles.policySection}>
              <div className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'fraud-services'}
                  language={language}
                  className={styles.copyBtn}
                />
                <h2>Fraud Services</h2>
                <p>
                  We will permanently remove fraudulent services and all efforts to facilitate
                  illegal behavior when we become aware of it. We use the term{' '}
                  <strong>fraud services</strong> to refer to coordinated activity that directly
                  attempts or is part of a larger attempt to fraudulently generate profit at the
                  expense of others. Profit can mean monetary or property gain secured from a third
                  party.
                </p>
                <p>
                  One activity disallowed under this policy is offering access to illicitly-obtained
                  goods — including, but not limited to: account credentials, credit card
                  information, personally identifiable information, and cracked commodity goods.
                  Coordinated efforts to defraud businesses, price gouging, forgery, and money
                  laundering are also not allowed.
                </p>
              </div>
            </section>

            <section className={styles.examplesSection}>
              <button
                className={styles.dropdownToggle}
                onClick={() => setFraudServicesExamplesOpen(!fraudServicesExamplesOpen)}
                aria-expanded={fraudServicesExamplesOpen}
                type="button"
              >
                <span>For example, you may not post, share, or engage in:</span>
                <span className={styles.dropdownIcon}>{fraudServicesExamplesOpen ? '−' : '+'}</span>
              </button>

              {fraudServicesExamplesOpen && (
                <div className={styles.dropdownContent}>
                  <ul>
                    <li>
                      Trading, selling, or attempting to purchase illicitly-obtained:
                      <ul>
                        <li>Account login information, such as usernames and passwords</li>
                        <li>
                          Financial information, such as credit card information and bank account
                          information
                        </li>
                        <li>
                          Personal information, such as email addresses, phone numbers, and social
                          security numbers
                        </li>
                      </ul>
                    </li>
                    <li>
                      Providing access to tools that facilitate account cracking or payment fraud
                    </li>
                    <li>
                      Participating in coordinated activities to defraud an individual, business, or
                      organization
                      <ul>
                        <li>
                          This includes making purchases with stolen payment details, exploiting
                          delivery logistics, socially-engineering company support staff, and
                          engaging in chargeback fraud
                        </li>
                      </ul>
                    </li>
                    <li>Providing instructions or lists of techniques on how to commit fraud</li>
                    <li>
                      Participating in an organized scheme to resell goods at a large profit, such
                      as price gouging
                    </li>
                    <li>
                      Selling, trading, or purchasing forgeries or participating in forgery
                      operations
                    </li>
                    <li>Participating in money laundering schemes</li>
                    <li>Trading, selling or distributing counterfeit goods</li>
                  </ul>
                </div>
              )}
            </section>

            <p>
              <a href="https://discord.com/safety/understanding-and-avoiding-common-scams">
                Recognising common scams
              </a>
            </p>
            <section id="how-to-avoid" className={styles.policySection}>
              <div className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'how-to-avoid'}
                  language={language}
                  className={styles.copyBtn}
                />
                <h2>How to Avoid Violating These Policies</h2>
                <p>
                  We take violations of these policies seriously and will take statements of desire
                  or intent to engage in illegal activities at face value.
                </p>
                <ul>
                  <li>
                    Don't send malicious links. This includes "IP grabbers" and other tools that try
                    to collect other people's sensitive information
                  </li>
                  <li>
                    Don't engage in fraudulent activities. This means somehow trying to generate a
                    profit at the expense of others
                  </li>
                  <li>
                    Don't participate in communities selling commodity goods that may have been
                    stolen or illicitly obtained
                  </li>
                </ul>
                <p>
                  Please report and leave servers dedicated to any of the violative content
                  described above. Discord may also take action against accounts that are present in
                  violative servers.
                </p>
              </div>
            </section>

            <section id="how-to-report" className={styles.policySection}>
              <div className={styles.sectionWrapper}>
                <CopyLinkButton
                  sectionId={'how-to-report'}
                  language={language}
                  className={styles.copyBtn}
                />
                <h2>How to Report Violations of These Policies</h2>
                <p>
                  Reporting safety violations is critically important to keeping you and the broader
                  Discord community safe. All Discord users can report policy violations right in
                  the app by following the instructions{' '}
                  <a href="/discord/safety/reporting-abusive-behavior-to-discord">here</a>. For more
                  information on these policies, please reference our{' '}
                  <a href="/discord/guidelines">Community Guidelines</a>{' '}
                  <GuidelineTooltip guidelineNumber="20" language={language}>
                    <a href="/discord/guidelines#guideline-20" className={styles.guidelineLink}>
                      #20
                    </a>
                  </GuidelineTooltip>
                  ,{' '}
                  <GuidelineTooltip guidelineNumber="21" language={language}>
                    <a href="/discord/guidelines#guideline-21" className={styles.guidelineLink}>
                      #21
                    </a>
                  </GuidelineTooltip>
                  , and{' '}
                  <GuidelineTooltip guidelineNumber="22" language={language}>
                    <a href="/discord/guidelines#guideline-22" className={styles.guidelineLink}>
                      #22
                    </a>
                  </GuidelineTooltip>
                  .
                </p>
              </div>
            </section>
          </>
        )}
      </div>

      {/* Tags section */}
      <div className={styles.tags}>
        <div className={styles.tagsLabel}>Tags</div>
        <div className={styles.tagsList}>
          <div className={styles.tag}>
            {language === 'cs' ? 'Integrita platformy' : 'Platform Integrity'}
          </div>
        </div>
      </div>
    </DiscordDocument>
  )
}

export default DiscordDeceptivePracticesPolicyPage
